/**
 * PATH: packages/job-hub/src/jobs/qa/qaProductResolve.ts
 * FILE_ID: JOBHUB-QA-PRODUCT-RESOLVE-001
 * PURPOSE: One product-list resolver for the build cell, phone card, and ITP.
 * WHY: Area, checklist, and step lists must agree with the backend resolver.
 */

export function resolvedProductNames(input: {
  itemNames?: string[] | null
  itemProduct?: string | null
  checklistNames?: string[] | null
  areaNames?: string[] | null
}): string[] {
  const own = (input.itemNames ?? []).map((name) => name.trim()).filter(Boolean)
  if (own.length) return own
  const single = (input.itemProduct ?? '').trim()
  if (single) return [single]
  const checklist = (input.checklistNames ?? []).map((name) => name.trim()).filter(Boolean)
  if (checklist.length) return checklist
  return (input.areaNames ?? []).map((name) => name.trim()).filter(Boolean)
}

export function joinProductNames(names: string[] | null | undefined): string {
  return (names ?? []).filter(Boolean).join(', ')
}

/** Own list when the target already has one, otherwise the inherited names. */
export function namesToEdit(own: string[] | null | undefined, inherited: string[]): string[] {
  const current = (own ?? []).map((name) => name.trim()).filter(Boolean)
  return current.length ? [...current] : [...inherited]
}

export function appendProductName(current: string[], name: string): string[] {
  const trimmed = name.trim()
  if (!trimmed) return current
  const key = trimmed.toLocaleLowerCase()
  if (current.some((row) => row.toLocaleLowerCase() === key)) return current
  return [...current, trimmed]
}

export function removeProductName(current: string[], name: string): string[] {
  const key = name.trim().toLocaleLowerCase()
  return current.filter((row) => row.toLocaleLowerCase() !== key)
}

export function replaceProductName(current: string[], from: string, to: string): string[] {
  const next = to.trim()
  if (!next) return removeProductName(current, from)
  const key = from.trim().toLocaleLowerCase()
  let replaced = false
  const rows = current.flatMap((row) => {
    if (row.toLocaleLowerCase() !== key) return [row]
    replaced = true
    return [next]
  })
  if (!replaced) return appendProductName(current, next)
  const seen = new Set<string>()
  return rows.filter((row) => {
    const folded = row.toLocaleLowerCase()
    if (seen.has(folded)) return false
    seen.add(folded)
    return true
  })
}
